import {
  FilterExpressionValue,
  LookupType,
  OdataFilterExpression,
  ODataFilterGroup,
  OdataFilterString,
} from '../../../../core';
import { signal, WritableSignal } from '@angular/core';
import { Observable, Subject } from 'rxjs';
import { DateUtils } from '../../../utils/date-utils';

export interface TableFilterConfig {
  title?: string;
  oDataPropertyName?: string;
  type: TableFilterType;
  filterFn?: (value: FilterExpressionValue, option: TableFilterOption) => string;
  options?: WritableSignal<TableFilterOption[]>;
  fetchResultsFn?: (searchText: string) => Observable<TableFilterOption[] | null>;
}

export class TableFilter {
  title: string;
  type: TableFilterType;
  oDataPropertyName: string;
  options: WritableSignal<TableFilterOption[]>;
  unsavedOptions?: TableFilterOption[];
  filterFn?: (value: FilterExpressionValue, option: TableFilterOption) => string;
  fetchResultsFn?: (searchText: string) => Observable<TableFilterOption[] | null>;
  resetAll$ = new Subject<void>();

  constructor(config?: TableFilterConfig) {
    this.title = config?.title ?? '';
    this.type = config?.type ?? TableFilterType.none;
    this.options = config?.options ?? signal([]);
    this.oDataPropertyName = config?.oDataPropertyName ?? '';
    this.fetchResultsFn = config?.fetchResultsFn;
    this.filterFn = config?.filterFn;
  }

  commitUnsavedOptions() {
    this.options.set(
      (this.unsavedOptions ?? []).map(tableFilterOption => {
        // need to convert the signal back to an ordinary value if we want to store it in a signal
        // (can't store signals inside of signals, but we needed a signal for reactive ui in a zoneless app)
        if (tableFilterOption.isSelected) {
          tableFilterOption.selected = tableFilterOption.isSelected();
          tableFilterOption.isSelected = undefined;
        }
        return tableFilterOption;
      }),
    );
  }

  resetUnsavedOptions() {
    this.resetAll$.next();
    if (
      this.type === TableFilterType.typeAhead ||
      this.type === TableFilterType.numeric ||
      this.type === TableFilterType.date ||
      this.type === TableFilterType.dateOnly
    ) {
      this.unsavedOptions = [];
      return;
    }

    this.unsavedOptions?.forEach(option => {
      option.isSelected?.set(false);
    });
  }

  hasSelectedOptions(): boolean {
    return this.options().some(option => option.selected);
  }

  buildODataFilterGroup(): ODataFilterGroup | null {
    if (!this.hasSelectedOptions()) {
      return null;
    }

    const expressions: (OdataFilterExpression | OdataFilterString)[] = [];
    this.options().forEach(option => {
      if (option.selected && option.value !== null) {
        if (this.filterFn) {
          // Custom Filter string logic
          const filterString: OdataFilterString = {
            filterString: this.filterFn(option.value, option),
          };
          expressions.push(filterString);
        } else {
          // Regular filter expression logic
          const expression: OdataFilterExpression = {
            field: this.oDataPropertyName,
            operator: 'eq',
            value: option.value,
            type: this.type,
          };

          if (option.isMaxValue) {
            expression.operator = 'le';
          } else if (option.isMinValue) {
            expression.operator = 'ge';
          }

          if (option.isExactValue && this.type === TableFilterType.date) {
            // Handle exact date values (start and end of selected date)
            expression.operator = 'ge';
            expressions.push({
              value: DateUtils.endOfDate(option.value as Date),
              field: this.oDataPropertyName,
              operator: 'le',
            });
          }
          expressions.push(expression);
        }
      }
    });

    const groupLogic =
      this.type === TableFilterType.numeric ||
      this.type === TableFilterType.date ||
      this.type === TableFilterType.dateOnly
        ? 'and'
        : 'or';
    return {
      logic: groupLogic,
      expressions: expressions,
    } as ODataFilterGroup;
  }
}

export interface TableFilterGroup {
  groupName: string;
  filters: TableFilter[];
}

export interface TableFilterOption {
  name: string;
  value: FilterExpressionValue | null;
  selected: boolean;
  // when working with this.unsavedoptions you'll want to use isSelected,
  // and when you're working with this.options you'll want to use selected
  isSelected?: WritableSignal<boolean>;
  isExactValue?: boolean;
  isMinValue?: boolean;
  isMaxValue?: boolean;
}

export class TableFilterOptionHelper {
  static fromLookupTypes(lookupTypes: LookupType[]): TableFilterOption[] {
    return (
      lookupTypes?.map(t => {
        return {
          name: t.name,
          value: t.id,
        } as TableFilterOption;
      }) ?? []
    );
  }
}

export enum TableFilterType {
  none,
  pillSelection,
  radio,
  checkbox,
  typeAhead,
  numeric,
  date,
  dateOnly,
}
